import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Card, CardContent } from '@guildeducationinc/recess';
import { WorkflowTasksList } from '@src/components/Admin/Workflows/WorkflowTasksList';
import { SAVE_WORKFLOW, WORKFLOWS } from '@src/graphql/queries/workflow';
import { WorkflowTaskTargetUserType, WorkflowTaskType, WorkflowType, } from '@src/types';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { camelCase, omit, uniqueId } from 'lodash';
import { Form, MessageType, Messages, useLocale } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import { z } from 'zod';
export const EditWorkflow = ({ workflow, onSave, onCancel }) => {
    const { t } = useLocale();
    const [saveWorkflow, { client }] = useMutation(SAVE_WORKFLOW);
    const workflowWithTempIds = useMemo(() => {
        return {
            ...workflow,
            workflowTasks: (workflow.workflowTasks || []).map(task => ({
                ...task,
                reminders: task.reminders || [],
                tempId: task.tempId || uniqueId('task-'),
            })),
        };
    }, [workflow]);
    const [messages, setMessages] = useState([]);
    const workflowValidation = z.object({
        name: z.string().min(1, { message: t('required') }),
        type: z.nativeEnum(WorkflowType, {
            message: t('required'),
        }),
        fallbackUserEmail: z
            .union([z.string().email({ message: t('must be a valid email') }), z.literal('')])
            .nullable()
            .optional(),
        minApprovalCount: z
            .union([
            z.string().max(0),
            z.number().positive({
                message: t('must be a positive number'),
            }),
            z.null(),
        ])
            .optional(),
        minRequiredApprovalCount: z
            .union([
            z.string().max(0),
            z.number().positive({
                message: t('must be a positive number'),
            }),
            z.null(),
        ])
            .optional(),
        workflowTasks: z.array(z
            .object({
            name: z.string().min(1, {
                message: t('required'),
            }),
            taskType: z.nativeEnum(WorkflowTaskType, {
                message: t('required'),
            }),
            sortOrder: z.number().min(1, {
                message: t('must be a positive number'),
            }),
            targetUserEmail: z.string().or(z.literal('')).or(z.null()).or(z.undefined()).optional(),
            targetUserType: z
                .nativeEnum(WorkflowTaskTargetUserType)
                .or(z.literal(''))
                .or(z.null())
                .or(z.undefined())
                .optional(),
            expiresAfter: z
                .number()
                .min(1, {
                message: t('must be a positive number'),
            })
                .or(z.string())
                .or(z.literal(''))
                .or(z.null())
                .or(z.undefined())
                .optional(),
            reminders: z
                .array(z.number())
                .optional()
                .or(z.null())
                .or(z.undefined())
                .transform(val => {
                // Coerce to proper format: null/undefined -> [], remove invalid values
                if (!val || !Array.isArray(val))
                    return [];
                return val.filter(v => typeof v === 'number' && !isNaN(v) && v > 0);
            }),
        })
            .superRefine((data, ctx) => {
            // Treat empty strings as "not set"
            const hasEmail = !!data.targetUserEmail && data.targetUserEmail.trim() !== '';
            const hasType = !!data.targetUserType && String(data.targetUserType).trim() !== '';
            let valid = true;
            // Can't have both
            if (hasEmail && hasType) {
                valid = false;
            }
            // Must have at least one
            else if (!hasEmail && !hasType) {
                valid = false;
            }
            if (!valid) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['targetUserEmail'],
                    message: t('Either Target User Email or Target User Type is required'),
                });
            }
            // Validate reminders (only if present and non-empty)
            if (data.reminders &&
                Array.isArray(data.reminders) &&
                data.reminders.length > 0 &&
                data.reminders.some(r => typeof r === 'number' && r > 0)) {
                if (!data.expiresAfter) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['reminders'],
                        message: t('Expiration date must be set to configure reminders'),
                    });
                }
                else {
                    const expiresAfterNum = typeof data.expiresAfter === 'string'
                        ? Number(data.expiresAfter)
                        : data.expiresAfter || 0;
                    if (!isNaN(expiresAfterNum) && expiresAfterNum > 0) {
                        for (const reminder of data.reminders) {
                            if (typeof reminder === 'number' && reminder >= expiresAfterNum) {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    path: ['reminders'],
                                    message: t('All reminder days must be less than expiration days'),
                                });
                                break;
                            }
                        }
                    }
                }
            }
        })),
    });
    const FIELDS = [
        {
            name: 'name',
            label: t('Workflow Name'),
            type: 'text',
        },
        {
            name: 'type',
            label: t('Workflow Type'),
            type: 'lookup',
            options: Object.values(WorkflowType).map(value => ({
                value,
                label: t(`WorkflowType.${camelCase(value)}`),
            })),
        },
        {
            name: 'fallbackUserEmail',
            label: t('Workflow Fallback Email'),
            type: 'text',
            helpText: _jsx("p", { children: "Fallback email used when the assigned approver is not found or inactive." }),
        },
        {
            name: 'minApprovalCount',
            label: t('Minimum Approval Count'),
            type: 'number',
            helpText: (_jsxs(_Fragment, { children: [_jsx("p", { children: "Total approvals needed (required + non-required)." }), _jsxs("p", { children: [_jsx("strong", { children: "Example:" }), " Set to 2 to need any 2 of 4 tasks approved."] }), _jsxs("p", { children: [_jsx("strong", { children: "Empty" }), " = at least 1."] }), _jsxs("p", { children: [_jsx("strong", { children: "Note:" }), " All required tasks must still be approved regardless of this count."] })] })),
            required: false,
            serializeValue(value) {
                return value ? String(value) : '';
            },
            deserializeValue(value) {
                const converted = Number(value);
                return !value || isNaN(converted) ? '' : converted;
            },
        },
        {
            name: 'minRequiredApprovalCount',
            label: 'Minimum Required Approval Count',
            type: 'number',
            helpText: (_jsxs(_Fragment, { children: [_jsx("p", { children: "How many \"Required\" tasks must approve." }), _jsxs("p", { children: [_jsx("strong", { children: "Example:" }), " Set to 1 so only 1 of 3 required approvers needs to approve."] }), _jsxs("p", { children: [_jsx("strong", { children: "Empty" }), " = all required tasks must approve."] })] })),
            required: false,
            serializeValue(value) {
                return value ? String(value) : '';
            },
            deserializeValue(value) {
                const converted = Number(value);
                return !value || isNaN(converted) ? '' : converted;
            },
        },
        {
            name: 'executeConcurrently',
            label: t('Execute Concurrently?'),
            type: 'boolean',
            serializeValue(value) {
                return value ? 'true' : 'false';
            },
            deserializeValue(value) {
                return value === 'true';
            },
        },
        {
            name: 'isActive',
            label: t('Active?'),
            type: 'boolean',
            serializeValue(value) {
                return value ? 'true' : 'false';
            },
            deserializeValue(value) {
                return value === 'true';
            },
        },
    ];
    const handleValidate = (workflow) => {
        const errors = {};
        const result = workflowValidation.safeParse(workflow);
        if (result.success === false) {
            for (const error of result.error.errors) {
                const errorPath = error.path.join('.');
                errors[errorPath] = error.message;
            }
            // Show validation errors
            if (Object.keys(errors).length > 0) {
                const errorList = Object.entries(errors)
                    .map(([path, msg]) => `${path}: ${msg}`)
                    .join('\n');
                setMessages([
                    {
                        type: MessageType.ERROR,
                        value: `Validation errors:\n${errorList}`,
                        params: {},
                    },
                ]);
            }
        }
        return errors;
    };
    const handleSave = async (workflow) => {
        setMessages([]);
        try {
            const input = prepareForSave(workflow);
            const result = await saveWorkflow({
                variables: {
                    input: {
                        ...omit(input, 'minApprovalCount', 'minRequiredApprovalCount'),
                        workflowTasks: input.workflowTasks.map((t) => omit(t, 'tempId')),
                        minApprovalCount: input.minApprovalCount ? Number(input.minApprovalCount) : undefined,
                        minRequiredApprovalCount: input.minRequiredApprovalCount
                            ? Number(input.minRequiredApprovalCount)
                            : undefined,
                        fallbackUserEmail: input.fallbackUserEmail || null,
                    },
                },
            });
            if (result.data?.saveWorkflow?.success) {
                if (onSave) {
                    onSave(result.data?.saveWorkflow);
                }
                client.refetchQueries({
                    include: [WORKFLOWS],
                });
            }
            else {
                setMessages(result.data?.saveWorkflow?.messages || []);
            }
        }
        catch (e) {
            setMessages([{ type: MessageType.ERROR, value: e.message }]);
        }
    };
    return (_jsxs("div", { children: [_jsx(Messages, { messages: messages }), _jsx(Form, { title: workflow.id ? 'Edit Workflow' : 'Add Workflow', modal: false, obj: workflowWithTempIds, fields: FIELDS, onValidate: handleValidate, onSave: handleSave, onCancel: onCancel, children: ({ draft, setDraft, errors }) => (_jsx(Card, { children: _jsx(CardContent, { children: _jsx(WorkflowTasksList, { workflow: draft, onChange: setDraft, errors: errors }) }) })) })] }));
};
